import { completeNewCustomerTaskAction, getNewCustomerTaskCenter, reportNewCustomerTaskAction } from '@user-center/api/newCustomerTask'
import store from '@user-center/store'
import { getToken } from '@user-center/utils/auth'
import { getCurrentLocale } from '@user-center/utils/i18n'
import { getStoreOtherJson } from '@user-center/utils/qiankun'
import { translateMallBatchTexts } from '@/utils/translate.js'

function isEnabled(value: unknown) {
  return value === true || value === 1 || value === '1' || value === 'true'
}

export function isNewCustomerTaskEnabled(): boolean {
  return isEnabled(
    store.getters.storeInfo?.offiaccountFollowData?.otherJson?.isNewCustomerTaskShop
    ?? getStoreOtherJson()?.isNewCustomerTaskShop,
  )
}

function getCacheKey(): string {
  let storedShopId = ''
  try {
    storedShopId = String(JSON.parse(window.localStorage.getItem('StoreInformation') || '{}')?.id || '')
  }
  catch {}
  const shopId = store.getters.storeInfo?.id || storedShopId || window.localStorage.getItem('shopId') || 'default'
  const userId = store.getters.userinfo?.id || store.getters.userinfo?.userId || getToken() || 'guest'
  return `newCustomerTaskCenter:${shopId}:${userId}`
}

function getCurrentShopId(): string {
  let storedShopId = ''
  try {
    storedShopId = String(JSON.parse(window.localStorage.getItem('StoreInformation') || '{}')?.id || '')
  }
  catch {}
  return String(store.getters.storeInfo?.id || storedShopId || window.localStorage.getItem('shopId') || '').trim()
}

function getCurrentUserId(): string {
  return String(store.getters.userinfo?.id || store.getters.userinfo?.userId || getToken() || 'guest')
}

function getPopupDismissKey(activityId: string | number): string {
  return `newCustomerTaskPopup:${getCurrentShopId() || 'default'}:${getCurrentUserId()}:${activityId}`
}

// "暂不参与" and the popup close button both suppress the prompt temporarily.
// Opening the task center only hides the current layer and does not write this timestamp.
export function isNewCustomerTaskPopupDismissed(activityId: string | number, dismissHours = 24): boolean {
  if (typeof window === 'undefined' || !activityId)
    return false
  const dismissedAt = Number(window.localStorage.getItem(getPopupDismissKey(activityId)) || 0)
  if (!dismissedAt)
    return false
  if (Date.now() - dismissedAt < dismissHours * 60 * 60 * 1000)
    return true
  window.localStorage.removeItem(getPopupDismissKey(activityId))
  return false
}

export function dismissNewCustomerTaskPopup(activityId: string | number): void {
  if (typeof window === 'undefined' || !activityId)
    return
  window.localStorage.setItem(getPopupDismissKey(activityId), String(Date.now()))
}

function readTaskCenterCache(): any {
  if (typeof window === 'undefined')
    return null
  try {
    const current = JSON.parse(window.localStorage.getItem(getCacheKey()) || 'null')
    if (current)
      return current
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index) || ''
      if (!key.startsWith('newCustomerTaskCenter:'))
        continue
      const cached = JSON.parse(window.localStorage.getItem(key) || 'null')
      if (cached?.activityId && Array.isArray(cached.taskList))
        return cached
    }
    return null
  }
  catch {
    return null
  }
}

export function cacheNewCustomerTaskCenter(activity: any): void {
  if (typeof window === 'undefined' || !activity)
    return
  const previous = readTaskCenterCache()
  window.localStorage.setItem(getCacheKey(), JSON.stringify({
    ...activity,
    localCompletedTaskIds: previous?.localCompletedTaskIds || [],
  }))
}

export async function translateNewCustomerTaskActivity(activity: any): Promise<any> {
  if (!activity || typeof window === 'undefined')
    return activity

  const locale = String(getCurrentLocale() || '')
  if (locale === 'zh-CN' || locale === 'zh-Hans')
    return activity

  // The batch service treats line breaks as independent translation segments.
  // Normalize every field before both sending and looking up its translation,
  // otherwise one task description can make its result count exceed the input count.
  const normalizeTranslateText = (value: any) => String(value || '').replace(/\s+/g, ' ').trim()
  const texts = [
    activity.benefitTitle,
    activity.benefitSubtitle,
    activity.unlockedBenefitText,
    activity.guestPrompt,
    activity.ruleDescription,
    ...(Array.isArray(activity.taskList)
      ? activity.taskList.flatMap((task: any) => [
        task.title,
        task.taskDescription,
        task.templateDesc,
        task.actionText,
        task.lockMessage,
        task.rewardTitle,
        ...(Array.isArray(task.rewardList) ? task.rewardList.map((reward: any) => reward?.title) : []),
      ])
      : []),
  ]
    .map(normalizeTranslateText)
    .filter(Boolean)

  if (!texts.length)
    return activity

  const { translatedMap } = await translateMallBatchTexts({
    texts,
    targetLanguage: locale,
    sourceLanguage: 'zh',
    appType: 'react-mall-app',
  })
  const translateText = (value: any) => translatedMap?.[normalizeTranslateText(value)] || value
  const translateTask = (task: any) => ({
    ...task,
    title: translateText(task.title),
    taskDescription: translateText(task.taskDescription),
    templateDesc: translateText(task.templateDesc),
    actionText: translateText(task.actionText),
    lockMessage: translateText(task.lockMessage),
    rewardTitle: translateText(task.rewardTitle),
    rewardList: Array.isArray(task.rewardList)
      ? task.rewardList.map((reward: any) => ({ ...reward, title: translateText(reward?.title) }))
      : task.rewardList,
  })

  return {
    ...activity,
    benefitTitle: translateText(activity.benefitTitle),
    benefitSubtitle: translateText(activity.benefitSubtitle),
    unlockedBenefitText: translateText(activity.unlockedBenefitText),
    guestPrompt: translateText(activity.guestPrompt),
    ruleDescription: translateText(activity.ruleDescription),
    taskList: Array.isArray(activity.taskList) ? activity.taskList.map(translateTask) : activity.taskList,
  }
}

function getEffectiveTaskList(activity: any): any[] {
  const localCompletedTaskIds = new Set<string>(activity?.localCompletedTaskIds || [])
  let previousCompleted = true
  const isOrdered = Number(activity?.sequenceMode) === 1
  return [...(Array.isArray(activity?.taskList) ? activity.taskList : [])]
    .sort((a, b) => Number(a.sequenceNo || 0) - Number(b.sequenceNo || 0))
    .map((task) => {
      const locallyCompleted = localCompletedTaskIds.has(task.taskTemplateId || '')
      const completed = task.actionStatus === 'COMPLETED' || locallyCompleted
      let effectiveTask = locallyCompleted && task.actionStatus !== 'COMPLETED'
        ? { ...task, actionStatus: 'COMPLETED' }
        : task
      if (isOrdered && previousCompleted && !completed && task.actionStatus === 'LOCKED') {
        effectiveTask = { ...task, actionStatus: 'AVAILABLE', actionText: undefined, lockMessage: null }
      }
      previousCompleted = completed
      return effectiveTask
    })
}

function showTaskRewardPopup(activity: any, eventType: string, bizNo: string | number): boolean {
  const task = getEffectiveTaskList(activity)
    .find(item => item.category === eventType && item.actionStatus === 'AVAILABLE')
  if (!task?.taskTemplateId || typeof window === 'undefined')
    return false

  const localCompletedTaskIds = new Set<string>(activity?.localCompletedTaskIds || [])
  localCompletedTaskIds.add(task.taskTemplateId)
  const nextActivity = { ...activity, localCompletedTaskIds: [...localCompletedTaskIds] }
  window.localStorage.setItem(getCacheKey(), JSON.stringify(nextActivity))
  const rewardText = task.rewardList?.map(item => item?.title).filter(Boolean).join('、') || task.rewardTitle
  window.dispatchEvent(new CustomEvent('new-customer-task-reward-popup', {
    detail: {
      activity: nextActivity,
      task: { ...task, rewardText },
      eventType,
      bizNo: String(bizNo),
      activityId: activity?.activityId,
    },
  }))
  return true
}

// Reporting deliberately runs independently of the underlying business request.
export async function reportNewCustomerTaskActionAsync(eventType: string, bizNo?: string | number): Promise<void> {
  if (!bizNo || !isNewCustomerTaskEnabled())
    return

  try {
    const response: any = await reportNewCustomerTaskAction({ param: { eventType, bizNo: String(bizNo) } })
    if (!response?.success)
      return
  }
  catch {
    return
  }

  const activity = readTaskCenterCache()
  if (!showTaskRewardPopup(activity, eventType, bizNo)) {
    const shopId = getCurrentShopId()
    if (shopId) {
      void getNewCustomerTaskCenter({ param: { shopId, channel: 'WEB' } })
        .then((response: any) => {
          if (!response?.success || !response?.data)
            return
          cacheNewCustomerTaskCenter(response.data)
          showTaskRewardPopup(response.data, eventType, bizNo)
        })
        .catch(() => undefined)
    }
  }
}

export function completeNewCustomerTaskActionAsync(eventType?: string, bizNo?: string | number, activityId?: string | number): Promise<unknown> {
  if (!eventType || !bizNo)
    return Promise.resolve(null)
  return completeNewCustomerTaskAction({
    param: {
      eventType,
      bizNo: String(bizNo),
      ...(activityId ? { activityId: String(activityId) } : {}),
    },
  })
}
